import { ResultV6HealthStatus, V6SupplierHealthStatus } from '../../webmodule-common/interop/interfaces/v6-config';
import { html, TemplateResult } from 'lit';
import { information } from '../../webmodule-common/other/ui/modal-option';

/** Scopes the wrap rules in dealer_ui.scss to just this dialog's reason block (@SUPOFF-HEALTH-002). */
export const v6HealthCheckMessageClass = 'v6-health-check-message';

/** The size passed to information() so this dialog fills the screen (@SUPOFF-HEALTH-005). */
const V6_HEALTH_CHECK_MODAL_SIZE = 'modal-fullscreen';

/** One "at Method(...) in File:line N" frame. */
export interface ParsedFrame {
  readonly method: string;
  readonly location: string;
}

/** A line that isn't a frame but still matters, e.g. "--- End of inner exception stack trace ---". */
export interface ParsedNote {
  readonly note: string;
}

export type DetailLine = ParsedFrame | ParsedNote;

export interface ParsedOfflineReason {
  /** Every message in the chain, type names stripped, joined into one plain-text readout
   *  (@SUPOFF-HEALTH-001). A cause that adds nothing the outer message doesn't already say is
   *  left out rather than repeated. */
  readonly headline: string;
  /** One entry per technical step, in the order they appeared. */
  readonly detail: DetailLine[];
}

// A .NET exception's first line is "Namespace.Qualified.TypeName: message". Requiring the type
// name to actually look like an exception type (namespace-qualified, ending in "Exception") is
// what tells this apart from free text that happens to contain a colon after something
// dotted - "supplier.example.com:443 did not answer" or "V6.Link: macro compile failed" are
// neither, and must fall back untouched rather than have "supplier.example.com" stripped off.
// The last segment's own prefix (the "InvalidOperation" in "InvalidOperationException") is
// optional: DealerLink throws plain `System.Exception` in more than one place, and that type
// name is just "Exception" with nothing of its own before it.
const EXCEPTION_TYPE_NAME =
  '[A-Za-z_]\\w*(?:\\.[A-Za-z_]\\w*)*\\.(?:[A-Za-z_]\\w*)?Exception(?:`\\d+)?(?:\\[[^\\]]*\\])?';
const EXCEPTION_HEADER = new RegExp(`^(?:${EXCEPTION_TYPE_NAME}):\\s+`);

// A real .NET Core inner-exception chain puts " ---> " on its own line, ahead of the wrapped
// exception's own header - never appended after the outer message on line 0 (that was the older
// .NET Framework shape this parser was first, wrongly, built against). An AggregateException's
// later children carry a "(Inner Exception #N)" label right after the arrow, and can start after
// its first child's frames have already begun - not only right after a header - so this is
// checked on every line, not only while still collecting a message.
const ARROW_LINE = /^--->\s*(.*)$/;
const INNER_EXCEPTION_LABEL = /^\(Inner Exception #\d+\)\s*/;

const END_INNER_NOTE = /^-{2,}\s*end of inner exception stack trace\s*-{2,}$/i;

// The method group is lazy and requires a closing ")" immediately before " in ", so it stops at
// the FIRST such point (the frame's own arg-list close), not the last - a file path containing
// " in " must not be swallowed into the method. A frame line must always have a "(...)" call
// shape; a message continuation that happens to start with "at " (e.g. "at least 3 retries were
// attempted") never does, so it is never mistaken for a frame.
const FRAME_WITH_LOCATION = /^at\s+(.+?\))\s+in\s+(.+)$/;
const FRAME_WITHOUT_LOCATION = /^at\s+(.+\))$/;

// AggregateException closes its last labelled child with "<---" glued straight onto whatever
// line it lands on - a frame when that child has its own stack, but its own header/message line
// when the child was constructed and never actually thrown (so it has no stack trace at all).
// Stripped up front, from every line, rather than only inside matchFrame.
const TRAILING_ARROW_MARKER = /\s*<---\s*$/;

function stripTrailingArrowMarker(text: string): string {
  return text.replace(TRAILING_ARROW_MARKER, '');
}

function stripLeadingType(text: string): string {
  return text.replace(EXCEPTION_HEADER, '');
}

function matchFrame(trimmed: string): ParsedFrame | null {
  const withLocation = FRAME_WITH_LOCATION.exec(trimmed);
  if (withLocation) return { method: withLocation[1].trim(), location: withLocation[2].trim() };

  const withoutLocation = FRAME_WITHOUT_LOCATION.exec(trimmed);
  if (withoutLocation) return { method: withoutLocation[1].trim(), location: '' };

  return null;
}

/**
 * True when `line` already says `causeText` as a wrapped cause, not merely shares letters with
 * it: the whole of `line` equals it, or `line` ends with it right after a ": "/". " separator (the
 * `$"{outer}: {inner.Message}"` idiom), or `line` contains it as a whole "(causeText)" segment
 * (AggregateException's own `"One or more errors occurred. (a) (b)"` form). Deliberately narrower
 * than a substring test: "denied" sitting inside "undenied-able", or "timeout" prefixing "timeout
 * while reading settings", must both survive, because neither is `line` actually stating the
 * cause, just sharing letters with it (@SUPOFF-HEALTH-001 amended, point 3).
 */
function statesCause(line: string, causeText: string): boolean {
  return (
    line === causeText ||
    line.endsWith(`: ${causeText}`) ||
    line.endsWith(`. ${causeText}`) ||
    line.includes(`(${causeText})`)
  );
}

/**
 * Joins the causes (outer message first, then each wrapped cause in order) into one headline. A
 * line identical to one the direct parent cause already has is dropped first (the common case
 * where an inner message is echoed verbatim as a message's own continuation line) - scoped to
 * just the parent so a phrase that legitimately recurs elsewhere is never silently eaten. What's
 * left of the cause is then dropped entirely if ANY ancestor - not only the direct parent -
 * already states it (see statesCause): a grandparent's message can echo the innermost cause
 * while skipping the one in between, and legibility means no cause is repeated anywhere in the
 * headline, not just immediately above it.
 */
function buildHeadline(causeLines: string[][]): string {
  const nonEmpty = causeLines.filter(lines => lines.length > 0);
  const causes: string[] = [];
  const ancestorLines: string[][] = [];

  nonEmpty.forEach((rawLines, index) => {
    if (index === 0) {
      const text = rawLines.join(' ').trim();
      if (text) causes.push(text);
      ancestorLines.push(rawLines);
      return;
    }

    const parentLines = ancestorLines[ancestorLines.length - 1];
    const newLines = rawLines.filter(line => !parentLines.includes(line));
    const causeText = newLines.join(' ').trim();

    if (causeText) {
      const alreadyStated = ancestorLines.some(lines => lines.some(line => statesCause(line, causeText)));
      if (!alreadyStated) causes.push(causeText);
    }

    ancestorLines.push(rawLines);
  });

  return causes.join('; caused by: ');
}

/**
 * Parses a .NET exception's ToString() text (today's shape of offlineReason) into a headline -
 * the full message, every line of it, up to the first frame or inner-exception marker - and one
 * detail line per technical step. Returns null for anything that doesn't start with a
 * recognisable exception header, so the caller falls back to the verbatim text: nothing this
 * can't parse is ever dropped, it just isn't reformatted.
 */
export function parseOfflineReason(reason: string): ParsedOfflineReason | null {
  const lines = reason.split(/\r\n|\n/);
  const first = stripTrailingArrowMarker((lines[0] ?? '').trim());
  if (!EXCEPTION_HEADER.test(first)) return null;

  const causeLines: string[][] = [];
  let cause: string[] = [stripLeadingType(first)];
  const detail: DetailLine[] = [];
  let inHeader = true;

  const closeCause = () => {
    causeLines.push(cause);
    cause = [];
  };

  for (let i = 1; i < lines.length; i++) {
    const trimmed = stripTrailingArrowMarker((lines[i] ?? '').trim());
    if (!trimmed) continue;

    // Checked before anything else, and regardless of inHeader: an AggregateException's later
    // children start a new wrapped cause wherever they appear, including after earlier frames.
    const arrow = ARROW_LINE.exec(trimmed);
    if (arrow) {
      closeCause();
      const nested = arrow[1].trim().replace(INNER_EXCEPTION_LABEL, '');
      cause = nested ? [stripLeadingType(nested)] : [];
      inHeader = true;
      continue;
    }

    if (inHeader) {
      if (END_INNER_NOTE.test(trimmed)) {
        closeCause();
        inHeader = false;
        detail.push({ note: trimmed });
        continue;
      }

      const frame = matchFrame(trimmed);
      if (frame) {
        closeCause();
        inHeader = false;
        detail.push(frame);
        continue;
      }

      cause.push(trimmed);
      continue;
    }

    const frame = matchFrame(trimmed);
    detail.push(frame ?? { note: trimmed });
  }

  closeCause();

  return { headline: buildHeadline(causeLines), detail };
}

/** Renders the parsed detail as monospace text: each frame is its method, then its location. */
function detailText(detail: DetailLine[]): string {
  return detail.map(d => ('note' in d ? d.note : d.location ? `${d.method}\n    ${d.location}` : d.method)).join('\n');
}

/** Builds the plain-English status line for one supplier - unchanged from before this order. */
function statusLineText(s: V6SupplierHealthStatus): string {
  const parts = [
    s.online ? 'Online' : 'Offline',
    s.upgrading ? 'upgrading' : '',
    s.reverseProxy
      ? `reverse-proxy, liveConnection: ${s.hasLiveConnection === null ? 'n/a' : s.hasLiveConnection}`
      : 'http'
  ].filter(Boolean);
  return parts.join(' - ');
}

/**
 * Renders one supplier's offlineReason: the parsed headline in normal proportional text, then the
 * detail (or, when it can't be parsed, the original text verbatim) inside a <pre> - set as text,
 * never HTML, so this needs no separate sanitising pass. A Copy action puts the exact original
 * offlineReason on the clipboard, byte for byte (@SUPOFF-HEALTH-004), independent of how it was
 * reformatted above it, and reports back whether it worked rather than failing silently - a
 * missing navigator.clipboard (a non-secure context) or a rejected write both surface as the same
 * visible failure message, and neither can escape as an unhandled rejection.
 */
function renderOfflineReason(reason: string): TemplateResult {
  const parsed = parseOfflineReason(reason);

  const copyEvent = async (e: Event) => {
    const status = (e.currentTarget as HTMLElement)
      .closest(`.${v6HealthCheckMessageClass}`)
      ?.querySelector<HTMLElement>('.v6-health-check-copy-status');
    if (!status) return;

    try {
      if (!navigator.clipboard) throw new Error('Clipboard access is not available in this context');
      await navigator.clipboard.writeText(reason);
      status.textContent = 'Copied';
      status.classList.remove('v6-health-check-copy-failed');
    } catch {
      status.textContent = 'Copy failed - select and copy the text below manually';
      status.classList.add('v6-health-check-copy-failed');
    }
  };

  const body = parsed
    ? html`
        <p>${parsed.headline}</p>
        ${parsed.detail.length ? html`<pre>${detailText(parsed.detail)}</pre>` : html``}
      `
    : html`<pre>${reason}</pre>`;

  return html`
    <div class="${v6HealthCheckMessageClass}">
      ${body}
      <div class="v6-health-check-copy-row">
        <webmodule-button size="small" variant="default" @click=${copyEvent}>Copy</webmodule-button>
        <span class="v6-health-check-copy-status" role="status"></span>
      </div>
    </div>
  `;
}

/**
 * Builds the V6 Health Check dialog shown to Cyncly staff (@SUPOFF-HEALTH-001, -002, -004). Each
 * supplier keeps its own status line, and gets its own reason block when offline with a reason.
 */
export function renderV6HealthCheckMessage(result: ResultV6HealthStatus): TemplateResult {
  const backstop = result.backstopLastRunUtc
    ? `Backstop last ran: ${new Date(result.backstopLastRunUtc).toLocaleString()}`
    : 'Backstop has never run on this process';

  return html`
    <div>
      <p>${backstop}</p>
      ${result.suppliers.map(
        s => html`
          <p><strong>${s.name}</strong>: ${statusLineText(s)}</p>
          ${s.offlineReason ? renderOfflineReason(s.offlineReason) : html``}
        `
      )}
    </div>
  `;
}

/**
 * Opens the V6 Health Check dialog full-screen (@SUPOFF-HEALTH-005). informationFn defaults to the
 * real information() and exists only so a test can capture the modalSize it's called with without
 * needing to drive an actual modal.
 */
export async function showV6HealthCheckDialog(
  result: ResultV6HealthStatus,
  informationFn: (
    message: TemplateResult,
    title?: string | TemplateResult,
    modalSize?: string
  ) => Promise<void> = information
): Promise<void> {
  await informationFn(renderV6HealthCheckMessage(result), 'V6 Health Check', V6_HEALTH_CHECK_MODAL_SIZE);
}
